import { ApiApplicationStats } from '@type/v1-api-types/talent-search';
import { UsersIcon } from '@heroicons/react/16/solid';
import { RadioCheckedIcon } from '@components/common/Icons';
import {
  Chip, LinkStyled,
  ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption,
} from '@components/common';
import { ListboxWrapper, ListboxWrapperProps } from '@components/common/Forms';
import { filterStyles } from '../helpers/filterStyles';


// Interfaces
// **************************************
type PickWrapperProps = 'recordFunc' | 'submitFunc' | 'idPrefix' | 'data' | 'queryParam';
type ApplicationStatsProps = Pick<ListboxWrapperProps<ApiApplicationStats>, PickWrapperProps> & {
  asLinks?: boolean;
  hideApplicationNumbers?: boolean;
  jobSlug?: string;

};


/**
 * @description Check if the option is disabled
 * @param option - the option (ApiApplicationStats) to check
 */
const optionDisabled = (option: ApiApplicationStats) => option.id !== -2 && option.nrApplications === 0;

/**
 * @description Check if the option is a link
 * @param option - the option (ApiApplicationStats) to check
 * @param asLinks - if the listbox options should all be links
 */
const optionIsLink = (option: ApiApplicationStats, asLinks: boolean) => {
  if (optionDisabled(option)) return false;
  if (asLinks) return true;
  return option.id < 0;
};

/**
 * @description Get the classes for the option
 * @param option - the option (ApiApplicationStats)
 * @param asLinks - if the listbox options should all be links
 */
const optionClasses = (option: ApiApplicationStats, asLinks: boolean) => {
  const baseClasses = optionIsLink(option, asLinks) ? filterStyles.listboxOptionLink : `${filterStyles.listboxOption} gap-1.5`;
  const disabledClasses = optionDisabled(option) ? ' !text-ink-light/60 !cursor-default' : '';
  return `${baseClasses}${disabledClasses}`;
};

/**
 * @description Get the path for the option if it is a link
 * @param option - the option (ApiApplicationStats)
 * @param jobSlug - the job slug
 */
const optionPath = (option: ApiApplicationStats, jobSlug: string) => {
  // job favorite candidates
  if (option.id === -1) return `/job/${jobSlug}/users-who-favorite-my-job`;
  // job similar candidates
  if (option.id === -2) return `/job/${jobSlug}/candidates`;
  // job applicants
  return `/job/${jobSlug}/applicants${option.id !== 0 ? `?status=${option.id}` : ''}`;
};


/**
 * @description ApplicationStats component - displays the stats for the applicants of a job (interview, rejected, etc.).
 * - **data**: ApiApplicationStats[] - the list of statuses for the applicants (ie: new, viewed, etc.)
 * - **recordFunc** - usBjForm record function
 * - **submitFunc** - usBjForm submit function
 * - **idPrefix** - the prefix for the id of the input field
 * - **queryParam** - the query param that holds the selected value; use it when you have an initial selected value from the query params
 * - **asLinks** - if the listbox options should all be links
 * - **jobSlug** - the job slug
 */
export const ApplicationStats = (props: ApplicationStatsProps) => {
  // Destruction props
  const {
    data,
    recordFunc,
    submitFunc,
    idPrefix,
    hideApplicationNumbers,
    queryParam = '0',
    asLinks = false,
    jobSlug,
  } = props;


  // Render component
  // **********************************************
  return (
    <ListboxWrapper
      fieldName="status"
      data={data}
      defaultValueKey="id"
      labelKey="label"
      recordFunc={recordFunc}
      submitFunc={submitFunc}
      idPrefix={idPrefix}
      queryParam={queryParam}
      recordOptions={{ convertValue: 'toNumber', dontHardReset: true }}
      renderListbox={({
        lsData, selected, setSelected, t,
      }) => {
        const selectedElement = selected as ApiApplicationStats;
        return (
          <ListboxStyled className="relative" by="id" value={selected} onChange={setSelected}>
            {({ open }) => (
              <>

                <ListboxStyledButton
                  as={Chip}
                  icon={<UsersIcon />}
                  isDropdownOpen={open}
                  withDropdownArrow
                >
                  {`${selectedElement.label}${selectedElement.hideNumber ? '' : ` (${selectedElement.nrApplications})`}`}
                </ListboxStyledButton>

                <ListboxStyledOptions
                  className={`${filterStyles.listboxOptionsWrapper} pb-2`}
                  animation="slideDown"
                  anchor="bottom start"
                >
                  <span className="inline-block p-3 text-sm text-ink-light">{`${t('global.label.display')}:`}</span>
                  {lsData.map((field) => (
                    <ListboxStyledOption
                      key={field.id}
                      value={field}
                      disabled={optionDisabled(field)}
                      className={optionClasses(field, asLinks)}
                    >
                      { // Non links
                        !optionIsLink(field, asLinks) && (
                          <>
                            <RadioCheckedIcon className={filterStyles.listboxOptionCheck} />
                            {`${field.label}${field.hideNumber || hideApplicationNumbers ? '' : ` (${field.nrApplications})`}`}
                          </>
                        )
                      }
                      { // Links
                        optionIsLink(field, asLinks) && jobSlug && (
                          <LinkStyled
                            styling="none"
                            href={optionPath(field, jobSlug)}
                            className={`${filterStyles.listboxOptionBase}`}
                          >
                            <>
                              <RadioCheckedIcon className={filterStyles.listboxOptionCheck} />
                              {`${field.label}${field.hideNumber ? '' : ` (${field.nrApplications})`}`}
                            </>
                          </LinkStyled>
                        )
                      }
                    </ListboxStyledOption>
                  ))}
                </ListboxStyledOptions>

              </>
            )}
          </ListboxStyled>
        );
      }}
    />
  );
};
